import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

const AppointmentDaySelector = ({ state, dispatch }) => {
  const [availableDays, setAvailableDays] = useState([]);

  useEffect(() => {
    const now = new Date();
    const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const days = [];
    for (let i = 0; days.length < 6; i++) {
      const currentDay = new Date(now.getTime() + i * 24 * 60 * 60 * 1000);
      if (currentDay.getDay() === 0) continue; // Skip Sunday
      const isToday = i === 0;
      days.push({
        name: isToday ? 'Today' : daysOfWeek[currentDay.getDay() - 1],
        date: currentDay.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' }),
        isToday,
        rawDate: currentDay.toISOString().split('T')[0]
      });
    }
    setAvailableDays(days);
  }, []);

  const handleCustomDateChange = (e) => {
    const selectedDate = new Date(e.target.value);
    const formattedDate = selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
    dispatch({ type: 'UPDATE_DAY', payload: formattedDate });
  };

  return (
    <div className="px-3 sm:px-6 pb-4 sm:pb-6 rounded-lg">
      {state.services.includes('taxi') && (
        <>
          <h2 className="text-xl sm:text-2xl font-semibold text-purple-700 text-center mb-2 sm:mb-4">
            Schedule Your Pet's Paw Taxi Ride 🚖🐾
          </h2>
          <p className="text-center text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">
            Choose the day that works best for your pet's ride, and let our Paw Taxi take your furry friend on a fun, safe journey!
          </p>
        </>
      )}

      {state.services.includes('walk') && (
        <>
          <h2 className="text-xl sm:text-2xl font-semibold text-purple-700 text-center mb-2 sm:mb-4">
            Choose a Day for Your Pet's Adventure 🐾
          </h2>
          <p className="text-center text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">
            Pick the perfect day that suits your furry friend's schedule, and let us create a delightful outing!
          </p>
        </>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
        {availableDays.map((day) => (
          <motion.div
            key={day.date}
            className={`p-4 sm:p-5 rounded-lg cursor-pointer transition-all duration-300 
              shadow-sm hover:shadow-md 
              ${state.day === day.date
                ? 'bg-gradientEnd text-white'
                : 'bg-bodyMain text-dark border border-gray-200'
              }`}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => dispatch({ type: 'UPDATE_DAY', payload: day.date })}
          >
            <div className="text-center space-y-1 sm:space-y-2">
              <p className={`text-base sm:text-lg font-medium
                ${day.isToday && state.day !== day.date ? 'text-primary underline' : ''}`}
              >
                {day.name}
              </p>
              <p className={`text-xs sm:text-sm 
                ${state.day === day.date ? 'text-white' : 'text-gray-500'}`}
              >
                {day.date}
              </p>
            </div>
          </motion.div>
        ))}

        {/* Custom Date Picker */}
        <div className="col-span-full p-2">
          <div className="w-full max-w-sm py-3 px-4 sm:px-8 mx-auto rounded-lg 
            cursor-pointer transition-all duration-300 
            shadow-sm hover:shadow-md
            bg-bodyMain border border-gray-200 
            hover:border-primary"
          >
            <div className="flex flex-col items-center justify-center h-full space-y-2">
              <p className="text-base sm:text-lg text-center font-medium text-dark">
                Pick a Different Date
              </p>
              <input
                type="date"
                className="w-full border border-gray-300 rounded-md 
                  text-center text-sm sm:text-base text-gray-700 
                  p-2 sm:p-3 cursor-pointer
                  focus:ring-2 focus:ring-primary focus:border-primary"
                min={availableDays[availableDays.length - 1]?.rawDate}
                onChange={handleCustomDateChange}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Selected Date Confirmation */}
      {state.day && (
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-4 sm:mt-6 text-center"
        >
          <p className="text-secondary text-base sm:text-lg font-medium">
            You've chosen: <span className="text-primary font-bold">{state.day}</span>
          </p>
          <p className="text-xs sm:text-sm text-gray-500 mt-1">
            We're excited to plan a pawsome adventure for your pet!
          </p>
        </motion.div>
      )}
    </div>
  );
};

export default AppointmentDaySelector;
